import React, { useMemo } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router-dom";

/**
 * Detect RR / fetch-style route errors (Response or { status, statusText, data }).
 */
export function getRouteErrorDetails(error) {
  if (error == null) {
    return {
      status: null,
      title: "Something went wrong",
      message: "An unexpected error occurred.",
      detail: "",
    };
  }

  if (typeof Response !== "undefined" && error instanceof Response) {
    const status = error.status;
    const title =
      status === 404
        ? "Page not found"
        : status >= 500
          ? "Server error"
          : "Request error";
    return {
      status,
      title,
      message: error.statusText || "The request could not be completed.",
      detail: "",
    };
  }

  if (typeof error === "object" && typeof error.status === "number") {
    const status = error.status;
    const title =
      status === 404
        ? "Page not found"
        : status >= 500
          ? "Server error"
          : "Something went wrong";
    let message =
      error.statusText ||
      (typeof error.data === "string" ? error.data : "") ||
      "The page could not be loaded.";
    if (
      message === "Not Found" ||
      message === ""
    ) {
      message =
        status === 404
          ? "This page doesn’t exist or has been moved."
          : message;
    }
    return {
      status,
      title,
      message,
      detail:
        error.data != null && typeof error.data !== "string"
          ? JSON.stringify(error.data)
          : "",
    };
  }

  if (error instanceof Error) {
    const msg = error.message || String(error);
    const chunk =
      /chunk|dynamically imported module|Loading CSS chunk|Failed to fetch/i.test(
        msg
      );
    return {
      status: null,
      title: chunk ? "App update" : "Something went wrong",
      message: chunk
        ? "A new version of the app is available. Refresh the page to continue."
        : msg,
      detail:
        process.env.NODE_ENV === "development" ? error.stack || "" : "",
    };
  }

  return {
    status: null,
    title: "Something went wrong",
    message: String(error),
    detail: "",
  };
}

function homeForPath(pathname) {
  if (pathname.startsWith("/admin")) {
    return { to: "/admin/dashboard", label: "Admin dashboard" };
  }
  if (pathname.startsWith("/master")) {
    return { to: "/master/dashboard", label: "Master dashboard" };
  }
  return { to: "/dashboard", label: "Dashboard" };
}

function ErrorPageFrame({
  status,
  title,
  message,
  detail,
  primaryAction,
  secondaryAction,
}) {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-12 bg-gray-50">
      <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
        {status != null && (
          <p className="text-5xl font-semibold text-gray-300 mb-2">{status}</p>
        )}
        <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 text-sm leading-relaxed mb-6">{message}</p>
        {detail ? (
          <pre className="text-left text-xs text-gray-500 bg-gray-100 rounded p-3 mb-6 overflow-auto max-h-40 whitespace-pre-wrap break-words">
            {detail}
          </pre>
        ) : null}
        <div className="flex flex-wrap gap-3 justify-center">
          {primaryAction}
          {secondaryAction}
        </div>
      </div>
    </div>
  );
}

/**
 * React Router `errorElement` — handles thrown Responses, route errors, and Error objects.
 */
export default function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { status, title, message, detail } = useMemo(
    () => getRouteErrorDetails(error),
    [error]
  );
  const home = homeForPath(pathname);

  const showDevDetails = process.env.NODE_ENV === "development" && detail;

  return (
    <ErrorPageFrame
      status={status}
      title={title}
      message={message}
      detail={showDevDetails ? detail : ""}
      primaryAction={
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Go back
        </button>
      }
      secondaryAction={
        <Link
          to={home.to}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {home.label}
        </Link>
      }
    />
  );
}

/**
 * Rendered for `path: "*"` when no child route matches (SPA 404).
 */
export function NotFoundPage({ context = "app" }) {
  const navigate = useNavigate();
  const links = {
    auth: { to: "/login", label: "Sign in" },
    app: { to: "/dashboard", label: "Dashboard" },
    admin: { to: "/admin/dashboard", label: "Admin dashboard" },
    master: { to: "/master/dashboard", label: "Master dashboard" },
  };
  const { to, label } = links[context] || links.app;

  return (
    <ErrorPageFrame
      status={404}
      title="Page not found"
      message="This URL doesn’t match any page in the application. Check the address or use a link below."
      detail=""
      primaryAction={
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Go back
        </button>
      }
      secondaryAction={
        <Link
          to={to}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {label}
        </Link>
      }
    />
  );
}

/**
 * Non-router render errors (class error boundary).
 */
export function UncaughtErrorPage({ error, onReset }) {
  const { title, message, detail } = getRouteErrorDetails(error);
  const showStack = process.env.NODE_ENV === "development" && detail;

  return (
    <ErrorPageFrame
      status={null}
      title={title}
      message={message}
      detail={showStack ? detail : ""}
      primaryAction={
        onReset ? (
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Try again
          </button>
        ) : null
      }
      secondaryAction={
        <Link
          to="/"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Home
        </Link>
      }
    />
  );
}

export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <UncaughtErrorPage
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
